import * as v from 'valibot';

/**
 * The assistant: the factory's front door, on whichever surface somebody is
 * already standing on.
 *
 * Everything reef knows — the context graph, the codebase index, the skills,
 * the runs — is reachable through the control plane, which means it is reachable
 * by people who know that reef has a control plane. That is a small number of
 * people. The assistant is the same capabilities behind a question in the
 * channel where the question was going to be asked anyway.
 *
 * A profile is not a new kind of thing. It is the same three facts every agent
 * in reef is made of — a versioned skill, the connections it may mount, and the
 * surfaces it answers on — which is what makes a team-specific assistant a
 * committed document somebody reviewed rather than a prompt somebody pasted.
 */

/**
 * Where an assistant answers.
 *
 * Three, and they are not interchangeable: Slack answers in a thread and is
 * read by a group, the CLI answers a person mid-task and is read once, the web
 * view answers next to the thing being asked about. The profile says which it
 * serves, because an answer written for one reads badly on the others.
 */
export const AssistantSurface = v.picklist(['slack', 'cli', 'web']);
export type AssistantSurface = v.InferOutput<typeof AssistantSurface>;

export const AssistantProfile = v.object({
  id: v.string(),
  name: v.string(),
  /** One line: who this profile is for. Shown in the picker and in `/reef ask --list`. */
  description: v.string(),
  /**
   * The group this profile belongs to, or null for the deployment-wide one.
   *
   * Resolved against live group membership, so a team profile is offered to the
   * team's members and to nobody else — the same authorization path the rest of
   * reef uses rather than a second one that drifts.
   */
  team: v.nullable(v.string()),
  /** The versioned skill that *is* this assistant's instructions. */
  skill: v.string(),
  /**
   * Skills mounted on top of the base one.
   *
   * A team profile is the base assistant plus what that team knows, not a fork
   * of it: a fork means the next improvement to the base assistant reaches one
   * of three hundred profiles.
   */
  extraSkills: v.optional(v.array(v.string()), []),
  /** Connection mounts, as `{ id, as }` — the same shape a step declares. */
  connections: v.optional(v.array(v.object({ id: v.string(), as: v.string() })), []),
  surfaces: v.optional(v.array(AssistantSurface), ['slack', 'cli', 'web']),
  /** Repository this profile answers about by default, when its team has one. */
  defaultRepo: v.optional(v.nullable(v.string()), null),
});
export type AssistantProfile = v.InferOutput<typeof AssistantProfile>;

/**
 * One question and what came back.
 *
 * `citations` is required to be non-empty for any answer that made a claim
 * about this deployment, and the assistant's own instructions say so. An
 * assistant that answers "the checkout service is owned by payments" with
 * nothing behind it is indistinguishable from one that guessed, and the second
 * kind is worse than no assistant.
 */
export const AssistantAnswer = v.object({
  sessionId: v.string(),
  profile: v.string(),
  surface: AssistantSurface,
  question: v.string(),
  answer: v.string(),
  /** Graph node ids, file paths, run ids — whatever the answer was read from. */
  citations: v.optional(v.array(v.string()), []),
  /** Skills that were in context. Recorded so the registry's usage counts include assistants. */
  skills: v.optional(v.array(v.string()), []),
  tookMs: v.optional(v.number(), 0),
  at: v.string(),
});
export type AssistantAnswer = v.InferOutput<typeof AssistantAnswer>;

export const AssistantStats = v.object({
  windowHours: v.number(),
  sessions: v.pipe(v.number(), v.integer()),
  profiles: v.pipe(v.number(), v.integer()),
  bySurface: v.optional(v.array(v.object({ surface: v.string(), sessions: v.number() })), []),
  byProfile: v.optional(v.array(v.object({ profile: v.string(), sessions: v.number() })), []),
  computedAt: v.string(),
});
export type AssistantStats = v.InferOutput<typeof AssistantStats>;
